/**
 * Addon set helpers shared by add-ons v2 / v3, cart.js and addon-set-reconcile.js.
 * A set is a main product line (`_lj_line_uid`) plus the addon lines that point
 * back at it (`_lj_relationship_1`, JSON-encoded). Both properties are private.
 */
(function () {
  'use strict';

  var LINE_UID_KEY = '_lj_line_uid';
  // Numbered so future relationship kinds can use `_lj_relationship_2`, `_3`, ...
  var RELATIONSHIPS_KEY = '_lj_relationship_1';
  var REL_TYPE_ADDON = 'addon';

  // Cart order of a set's addons under the main product, by `addon_type`; unlisted types go last
  var ADDON_TYPE_ORDER = [
    'leg_option',
    'chair_option',
    'mattress',
    'furniture_cover',
    'parasol',
    'parasol_base',
    'parasol_light',
    'lamp_shade'
  ];

  // crypto.randomUUID is unavailable on plain-http previews, so fall back to a random id of the same shape
  function mintLineUid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (char) {
      var random = (Math.random() * 16) | 0;
      var value = char === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  function buildRelationship(parentLineUid, relClass, relType) {
    return JSON.stringify([
      {
        parentLineUid: parentLineUid,
        relType: relType || REL_TYPE_ADDON,
        relClass: relClass || null
      }
    ]);
  }

  function getProperties(item) {
    return (item && item.properties) || {};
  }

  function getLineUid(item) {
    var value = getProperties(item)[LINE_UID_KEY];
    return value ? String(value) : null;
  }

  function parseRelationships(item) {
    var raw = getProperties(item)[RELATIONSHIPS_KEY];
    if (!raw) return [];

    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[addon-set] could not parse ' + RELATIONSHIPS_KEY, error);
      return [];
    }
  }

  function getParentLineUid(item) {
    var relationships = parseRelationships(item);

    for (var i = 0; i < relationships.length; i++) {
      var relationship = relationships[i];
      if (relationship && relationship.relType === REL_TYPE_ADDON && relationship.parentLineUid) {
        return String(relationship.parentLineUid);
      }
    }

    return null;
  }

  // Always 1 today; read with a default so a future ratio needs no cart migration
  function getQtyRatio(item) {
    var relationships = parseRelationships(item);
    var ratio = relationships.length ? relationships[0].qtyRatio : null;
    var parsed = parseInt(ratio, 10);
    return parsed > 0 ? parsed : 1;
  }

  // Main variant id plus sorted addon variant ids, so selection order does not matter
  function buildComposition(mainVariantId, addonVariantIds) {
    var addons = (addonVariantIds || []).map(String).sort();
    return String(mainVariantId) + ':' + addons.join('-');
  }

  function addonTypeRank(addonType) {
    var rank = ADDON_TYPE_ORDER.indexOf(addonType);
    return rank === -1 ? ADDON_TYPE_ORDER.length : rank;
  }

  // [addonType, addon] entries in /cart/add.js order. Shopify puts the last item of a
  // multi-item add on top, so the ranking is reversed here; the main product goes after these.
  function orderForAdd(entries) {
    return (entries || [])
      .slice()
      .sort(function (a, b) {
        return addonTypeRank(a[0]) - addonTypeRank(b[0]);
      })
      .reverse();
  }

  function findChildren(cart, lineUid) {
    if (!cart || !cart.items || !lineUid) return [];

    return cart.items.filter(function (item) {
      return getParentLineUid(item) === lineUid;
    });
  }

  function findSets(cart) {
    if (!cart || !cart.items) return [];

    return cart.items.reduce(function (sets, item) {
      var lineUid = getLineUid(item);
      if (!lineUid) return sets;

      var children = findChildren(cart, lineUid);

      sets.push({
        lineUid: lineUid,
        parent: item,
        children: children,
        composition: buildComposition(
          item.variant_id,
          children.map(function (child) {
            return child.variant_id;
          })
        )
      });

      return sets;
    }, []);
  }

  function sameOtherProperties(a, b) {
    var left = a || {};
    var right = b || {};
    var keys = Object.keys(left).concat(Object.keys(right));

    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (key === LINE_UID_KEY || key === RELATIONSHIPS_KEY) continue;

      var leftValue = left[key] == null ? '' : String(left[key]);
      var rightValue = right[key] == null ? '' : String(right[key]);
      if (leftValue !== rightValue) return false;
    }

    return true;
  }

  // Shopify only merges an add into existing lines when every property matches
  function itemsMergeInto(set, items) {
    var unused = set.children.slice();

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var properties = item.properties || {};

      if (properties[LINE_UID_KEY]) {
        if (String(set.parent.variant_id) !== String(item.id)) return false;
        if (!sameOtherProperties(set.parent.properties, properties)) return false;
        continue;
      }

      var match = -1;
      for (var j = 0; j < unused.length; j++) {
        if (
          String(unused[j].variant_id) === String(item.id) &&
          sameOtherProperties(unused[j].properties, properties)
        ) {
          match = j;
          break;
        }
      }

      if (match === -1) return false;
      unused.splice(match, 1);
    }

    return true;
  }

  // Pass `items` to only match a set the new items would actually merge into
  function findMatchingSet(cart, composition, items) {
    var sets = findSets(cart);

    for (var i = 0; i < sets.length; i++) {
      if (sets[i].composition !== composition) continue;
      if (items && !itemsMergeInto(sets[i], items)) continue;
      return sets[i];
    }

    return null;
  }

  // Not used for adds: /cart/update.js skips the stock check on lines already in the cart
  function buildSetIncrement(set, addQuantity) {
    var updates = {};

    updates[set.parent.key] = set.parent.quantity + addQuantity;

    set.children.forEach(function (child) {
      updates[child.key] = child.quantity + addQuantity * getQtyRatio(child);
    });

    return updates;
  }

  // Point new items at an existing set's uid so /cart/add.js merges them into it
  function retargetSet(items, parentLineUid) {
    (items || []).forEach(function (item) {
      var properties = item && item.properties;
      if (!properties) return;

      if (properties[LINE_UID_KEY]) {
        properties[LINE_UID_KEY] = parentLineUid;
        return;
      }

      if (!properties[RELATIONSHIPS_KEY]) return;

      var relationships = parseRelationships(item).map(function (relationship) {
        if (relationship && relationship.relType === REL_TYPE_ADDON) {
          relationship.parentLineUid = parentLineUid;
        }
        return relationship;
      });

      properties[RELATIONSHIPS_KEY] = JSON.stringify(relationships);
    });

    return items;
  }

  function fetchCart() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    return fetch(root + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (response) {
      if (!response.ok) throw new Error('Could not read cart: ' + response.status);
      return response.json();
    });
  }

  window.AddonSetRelationships = {
    LINE_UID_KEY: LINE_UID_KEY,
    RELATIONSHIPS_KEY: RELATIONSHIPS_KEY,
    REL_TYPE_ADDON: REL_TYPE_ADDON,
    ADDON_TYPE_ORDER: ADDON_TYPE_ORDER,
    orderForAdd: orderForAdd,
    mintLineUid: mintLineUid,
    buildRelationship: buildRelationship,
    parseRelationships: parseRelationships,
    getLineUid: getLineUid,
    getParentLineUid: getParentLineUid,
    getQtyRatio: getQtyRatio,
    buildComposition: buildComposition,
    findChildren: findChildren,
    findSets: findSets,
    findMatchingSet: findMatchingSet,
    buildSetIncrement: buildSetIncrement,
    retargetSet: retargetSet,
    fetchCart: fetchCart
  };
})();
